import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'react-bootstrap';
import DatePicker from '../../components/forms/formDatePicker';
import { useState } from 'react';
import EmployeeSelect from '../../components/selects/employeeSelect';
import FormInput from '../../components/forms/formInput';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { onMutateSuccess, onQuerySuccess } from '../../utils/queryResponse';
import { createPayroll, getPayrollById, updatePayroll } from '../../services/payrolls';
import { rsdFormat } from '../../utils/helper';
import OverlayLoader from '../../components/overlayLoader';
import SbrDrawer from '../../components/sbr/SbrDrawer';

const WalletIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" /><path d="M3 5v14a2 2 0 0 0 2 2h16v-5" /><path d="M18 12a2 2 0 0 0 0 4h4v-4Z" /></svg>
);

const initPayroll = {
    payStartDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    payEndDate: new Date(),
    employeeId: "",
    workingPeriod: "",
    rateType: "HOURLY",
    rate: '',
    amount: '',
    bonus: '',
    deduction: '',
    salary: '',
    notes: ""
}

const SetPayRollModal = ({
    display,
    toggle,
    payrollId,
}) => {

    const { t } = useTranslation();
    const [payroll, setPayroll] = useState(initPayroll)

    const queryClient = useQueryClient()
    const { mutate, isLoading } = useMutation(payrollId ? updatePayroll : createPayroll, {
        onSuccess: onMutateSuccess(() => {
            queryClient.invalidateQueries(['payrolls', 'paginated'])
            onHide()
        })
    })

    const { data, isFetching } = useQuery(['payrolls', 'details', payrollId], () => payrollId && getPayrollById(payrollId), {
        staleTime: 1000 * 60 * 2,
    })

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            setPayroll(result)
        })
    }, [data])

    const onShow = () => {
        !payrollId && setPayroll(initPayroll)
    }

    const onHide = () => {
        toggle(false)
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        e.stopPropagation()

        let _payroll = payroll
        _payroll.amount = getPayrollAmount()
        _payroll.salary = getSalary()
        mutate(_payroll)
    }

    const handleChange = (objValue) => {
        const _payroll = { ...payroll, ...objValue }
        setPayroll(_payroll)
    }

    const getWorkingPeriodLabel = () => {
        let _rateType = payroll.rateType
        if (_rateType === "HOURLY")
            return t('payroll.workingHours')
        else if (_rateType === "DAILY")
            return t('payroll.workingDays')
        else
            return t('payroll.workingPeriod')
    }

    const handleChangeRateType = (value) => {
        let _change = {
            rateType: value
        }
        if (value === "FIXED_MONTHLY") {
            _change.workingPeriod = ""
            _change.rate = 0
        }
        handleChange(_change)
    }

    const getPayrollAmount = () => {
        return payroll.rateType === "FIXED_MONTHLY" ? parseFloat(payroll.amount) : parseFloat(payroll.workingPeriod) * parseFloat(payroll.rate)
    }

    const getSalary = () => {
        return (getPayrollAmount() + parseFloat(payroll?.bonus || 0)) - parseFloat(payroll?.deduction || 0)
    }

    return (
        <SbrDrawer
            show={display}
            onHide={onHide}
            onShow={onShow}
            backdrop={isLoading ? "static" : true}
            title={payrollId ? t('payroll.editPayroll') : t('payroll.addPayroll')}
            subtitle={payrollId ? t('payroll.updateEntry') : t('payroll.createEntry')}
            icon={<WalletIcon />}
            footer={
                <>
                    <div className="drawer-total">
                        <span className="l">{t('payroll.salary')}</span>
                        <span className="v">{rsdFormat(getSalary())}</span>
                    </div>
                    <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={onHide} disabled={isLoading}>{t('common.cancel')}</button>
                    <button type='submit' form="set-payroll" className="btn-sbr btn-primary-sbr btn-sm-sbr" disabled={isLoading}>
                        {isLoading ? t('common.saving') : t('common.save')}
                    </button>
                </>
            }
        >
            <Form id='set-payroll' onSubmit={handleSubmit} style={{ position: 'relative' }}>
                <Row className="mb-3">
                    <Col lg="6">
                        <DatePicker className='form-control mb-2' label={t('payroll.payStartDate')} placeholderText={t('payroll.payStartDate')}
                            selected={new Date(payroll?.payStartDate) ?? null} onChange={(date) => handleChange({ payStartDate: date })} dateFormat="dd.MM.yyyy"
                        />
                    </Col>
                    <Col lg="6">
                        <DatePicker className='form-control mb-2' label={t('payroll.payEndDate')} placeholderText={t('payroll.payEndDate')}
                            selected={new Date(payroll?.payEndDate) ?? null} onChange={(date) => handleChange({ payEndDate: date })} dateFormat="dd.MM.yyyy"
                        />
                    </Col>
                    <Col lg="6">
                        <EmployeeSelect required className="mb-2" value={payroll.employeeId} label={t('payroll.employee')} placeholder={t('payroll.employee')} onChange={(option) => handleChange({ employeeId: option.value })} />
                    </Col>
                    <Col lg="6">
                        <FormInput as="select" label={t('payroll.rateType')} placeholder={t('payroll.selectRateType')}
                            value={payroll.rateType} onChange={(e) => handleChangeRateType(e.target.value)}>
                            <option value="HOURLY">{t('payroll.hourly')}</option>
                            <option value="DAILY">{t('payroll.daily')}</option>
                            <option value="FIXED_MONTHLY">{t('payroll.fixedMonthly')}</option>
                        </FormInput>
                        {/* <FormInput className="mb-2" label="Rate Type" value={payroll.rateType} onChange={(e) => handleChange({ rateType: e.target.value })} /> */}
                    </Col>
                    <Col lg="6">
                        <FormInput disabled={payroll.rateType === "FIXED_MONTHLY"} type="number" className="mb-2" label={getWorkingPeriodLabel()} value={payroll.workingPeriod} onChange={(e) => handleChange({ workingPeriod: e.target.value })} />
                    </Col>
                    <Col lg="6">
                        <FormInput disabled={payroll.rateType === "FIXED_MONTHLY"} type="number" className="mb-2" label={t('payroll.rate')} value={payroll.rate} onChange={(e) => handleChange({ rate: e.target.value })} />
                    </Col>
                    <Col lg="12">
                        <FormInput disabled={payroll.rateType !== "FIXED_MONTHLY"} type="number" className="mb-2" label={t('common.amount')} value={getPayrollAmount()} onChange={(e) => handleChange({ amount: e.target.value })} />
                    </Col>
                    <Col lg="12">
                        <Row>
                            <Col lg="6">
                                <FormInput type="number" className="mb-2" label={t('payroll.bonus')} value={payroll.bonus} onChange={(e) => handleChange({ bonus: e.target.value })} />
                            </Col>
                            <Col lg="6">
                                <FormInput type="number" className="mb-2" label={t('payroll.deductions')} value={payroll.deduction} onChange={(e) => handleChange({ deduction: e.target.value })} />
                            </Col>
                        </Row>
                    </Col>
                    <Col lg="12">
                        <FormInput as="textarea" label={t('common.notes')} value={payroll.notes} onChange={(e) => handleChange({ notes: e.target.value })} />
                    </Col>
                </Row>

                <Row className="mt-2 mb-3">
                    <Col>
                        <h5 className="d-flex text-end justify-content-between">
                            <span>{t('payroll.salary')}: </span>
                            <span>{rsdFormat(getSalary())}</span>
                        </h5>
                    </Col>
                </Row>

                {isFetching &&
                    <OverlayLoader />
                }

            </Form>
        </SbrDrawer>
    );
};

export default SetPayRollModal;
